import React, { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from './cn';
import { Icon } from './base/Icon';

/**
 * SearchSelect — ช่องเลือกหนึ่งรายการที่พิมพ์ค้นหาได้ (combobox)
 *
 * ใช้แทน `<select>` เมื่อรายการยาวจนเลื่อนหาไม่ไหว เช่นแพ็กเกจของหน่วยงาน
 *
 * ค้นได้สองแบบ:
 *   — ไม่ส่ง `loadOptions` → กรอง `options` ในเบราว์เซอร์ (รายการทั้งหมดอยู่ในมือแล้ว)
 *   — ส่ง `loadOptions`   → ถาม server ทุกครั้งที่พิมพ์ (หน่วง 300ms) ใช้เมื่อรายการมากเกินดึงมาครั้งเดียว
 *
 * ⚠️ รายการที่เลือกอยู่อาจไม่อยู่ใน `options` (เช่นแพ็กเกจที่ถูกปิดไปแล้ว หรือมาจากผลค้นที่ server)
 *    ผู้เรียกต้องส่ง `selectedLabel` มาด้วย ไม่งั้นช่องจะดูเหมือนยังไม่ได้เลือก
 *
 * คีย์บอร์ด: ↑ ↓ เลื่อน · Enter เลือก · Esc ปิดและคืนค่าเดิม
 */

export interface SearchSelectOption<V extends string | number = string | number> {
  value: V;
  label: string;
  /** บรรทัดรอง เช่นรหัส — ถูกค้นด้วย */
  description?: string;
  /** ภาพย่อหน้ารายการ */
  imageUrl?: string | null;
}

export interface SearchSelectProps<V extends string | number> {
  value: V | null;
  onChange: (value: V | null, option: SearchSelectOption<V> | null) => void;
  options: Array<SearchSelectOption<V>>;
  /** ค้นที่ server แทนการกรองในเบราว์เซอร์ */
  loadOptions?: (keyword: string) => Promise<Array<SearchSelectOption<V>>>;
  /** ข้อความของค่าที่เลือกอยู่ เมื่อค่านั้นไม่อยู่ใน `options` */
  selectedLabel?: string;
  placeholder?: string;
  /** ข้อความสำหรับ screen reader — ใช้เมื่อไม่ได้ผูกกับ <label> */
  ariaLabel?: string;
  id?: string;
  emptyText?: string;
  disabled?: boolean;
  className?: string;
}

let idSeq = 0;

const matches = (o: SearchSelectOption, q: string) => {
  const k = q.trim().toLowerCase();
  if (!k) return true;
  return o.label.toLowerCase().includes(k) || (o.description ?? '').toLowerCase().includes(k);
};

export function SearchSelect<V extends string | number>({
  value, onChange, options, loadOptions, selectedLabel, placeholder = 'พิมพ์เพื่อค้นหา',
  ariaLabel, id, emptyText = 'ไม่พบรายการที่ตรงกับคำค้น', disabled, className,
}: SearchSelectProps<V>) {
  /* React 16 ของ host ไม่มี useId — useRef ให้ค่าคงที่ตลอดอายุ component */
  const autoId = useRef(`bo-ss-${(idSeq += 1)}`).current;
  const inputId = id ?? autoId;
  const listId = `${inputId}-list`;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [remote, setRemote] = useState<Array<SearchSelectOption<V>> | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  /** ตัวที่เพิ่งเลือก — ผลค้นจาก server ถูกล้างเมื่อเปิดช่องใหม่ ต้องจำชื่อไว้เอง */
  const picked = useRef<SearchSelectOption<V> | null>(null);

  const selected = useMemo(
    () => options.find((o) => o.value === value)
      ?? remote?.find((o) => o.value === value)
      ?? (picked.current?.value === value ? picked.current : null),
    [options, remote, value],
  );
  const shownLabel = selected?.label ?? (value != null ? selectedLabel ?? String(value) : '');

  /* ค้นที่ server — หน่วงไว้ไม่ให้ยิงทุกตัวอักษร และทิ้งผลของคำค้นเก่าที่ตอบกลับช้า */
  useEffect(() => {
    if (!loadOptions || !open) return;
    const k = query.trim();
    if (!k) { setRemote(null); setLoading(false); setLoadFailed(false); return; }
    let stale = false;
    setLoading(true);
    const t = window.setTimeout(() => {
      loadOptions(k)
        .then((r) => { if (!stale) { setRemote(r); setLoadFailed(false); } })
        .catch(() => { if (!stale) { setRemote([]); setLoadFailed(true); } })
        .finally(() => { if (!stale) setLoading(false); });
    }, 300);
    return () => { stale = true; window.clearTimeout(t); };
  }, [loadOptions, open, query]);

  const list = useMemo(() => {
    if (loadOptions && query.trim()) return remote ?? [];
    return options.filter((o) => matches(o, query));
  }, [loadOptions, remote, options, query]);

  useEffect(() => { setActive(0); }, [query, open]);

  /* ให้รายการที่ไฮไลต์อยู่ในจอเสมอเมื่อเลื่อนด้วยคีย์บอร์ด */
  useEffect(() => {
    if (!open) return;
    listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  const close = () => { setOpen(false); setQuery(''); };

  const pick = (o: SearchSelectOption<V>) => {
    picked.current = o;
    onChange(o.value, o);
    close();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      setActive((i) => Math.min(i + 1, list.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      const o = list[active];
      if (open && o) { e.preventDefault(); pick(o); }
    } else if (e.key === 'Escape') {
      if (open) { e.preventDefault(); close(); }
    }
  };

  const status = loading
    ? 'กำลังค้นหา…'
    : loadFailed
      ? 'ค้นหาไม่สำเร็จ ลองพิมพ์ใหม่อีกครั้ง'
      : list.length === 0
        ? emptyText
        : null;

  return (
    <div className={cn('relative w-full', className)}>
      <div className="relative">
        <Icon
          name="search"
          size={18}
          className="pointer-events-none absolute left-[var(--space-25)] top-1/2 -translate-y-1/2 text-[var(--ink-400)]"
        />
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          role="combobox"
          aria-label={ariaLabel}
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={open && list[active] ? `${listId}-${active}` : undefined}
          autoComplete="off"
          disabled={disabled}
          value={open ? query : shownLabel}
          placeholder={open && shownLabel ? shownLabel : placeholder}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          onBlur={close}
          onChange={(e) => {
            const text = e.target.value;
            /* ปิดอยู่ = ช่องแสดงชื่อของที่เลือกไว้ และยังโฟกัสอยู่ (เช่นเพิ่งเลือกเสร็จ)
               พิมพ์ต่อต้องเริ่มคำค้นใหม่ ไม่ใช่ต่อท้ายชื่อเดิม */
            setQuery(open ? text : text.startsWith(shownLabel) ? text.slice(shownLabel.length) : '');
            setOpen(true);
          }}
          onKeyDown={onKeyDown}
          className="min-h-[var(--hit-control)] w-full rounded-[var(--radius-sm)] border border-[var(--line-300)] bg-[var(--surface-0)] pl-[var(--space-8)] pr-[var(--space-8)] font-[family-name:var(--font-thai)] text-[length:var(--text-form)] leading-[var(--leading-form)] text-[var(--ink-900)] placeholder:text-[var(--ink-400)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-[var(--bg-disabled)] disabled:text-[var(--text-disabled)]"
        />
        {value != null && !disabled ? (
          <button
            type="button"
            aria-label="ล้างค่าที่เลือก"
            /* mousedown ไม่ให้ช่องเสียโฟกัสก่อน click ทำงาน */
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => { onChange(null, null); close(); inputRef.current?.focus(); }}
            className="absolute right-[var(--space-1)] top-1/2 inline-flex h-[var(--hit-icon-sm)] w-[var(--hit-icon-sm)] -translate-y-1/2 items-center justify-center rounded-[var(--radius-sm)] text-[var(--ink-600)] outline-none hover:bg-[var(--surface-100)] focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
          >
            <Icon name="close" size={16} />
          </button>
        ) : (
          <Icon
            name="expand_more"
            size={20}
            className="pointer-events-none absolute right-[var(--space-2)] top-1/2 -translate-y-1/2 text-[var(--ink-600)]"
          />
        )}
      </div>

      {open && (
        <div className="absolute left-0 right-0 z-20 mt-[var(--space-1)] overflow-hidden rounded-[var(--radius-md)] border border-[var(--line-200)] bg-[var(--surface-0)] shadow-[var(--shadow-md)]">
          {status && (
            <p role="status" className="m-0 px-[var(--space-3)] py-[var(--space-2)] text-[length:var(--text-form)] text-[var(--ink-700)]">
              {status}
            </p>
          )}
          <ul
            ref={listRef}
            id={listId}
            role="listbox"
            aria-label={ariaLabel}
            className="m-0 max-h-[320px] list-none overflow-auto p-[var(--space-1)]"
          >
            {!loading && list.map((o, i) => {
              const isSelected = o.value === value;
              const isActive = i === active;
              return (
                <li
                  key={String(o.value)}
                  id={`${listId}-${i}`}
                  data-index={i}
                  role="option"
                  aria-selected={isSelected}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(o)}
                  onMouseEnter={() => setActive(i)}
                  className={cn(
                    'flex cursor-pointer items-center gap-[var(--space-2)] rounded-[var(--radius-sm)] px-[var(--space-2)] py-[var(--space-15)]',
                    isActive ? 'bg-[var(--blue-050)]' : 'bg-[var(--surface-0)]',
                  )}
                >
                  {o.imageUrl !== undefined && (
                    o.imageUrl ? (
                      <img src={o.imageUrl} alt="" className="h-[36px] w-[36px] shrink-0 rounded-[var(--radius-sm)] object-cover" />
                    ) : (
                      <span className="flex h-[36px] w-[36px] shrink-0 items-center justify-center rounded-[var(--radius-sm)] bg-[var(--surface-100)]">
                        <Icon name="image_not_supported" size={16} className="text-[var(--ink-400)]" />
                      </span>
                    )
                  )}
                  <span className="flex min-w-0 flex-1 flex-col leading-[1.35]">
                    <span className="text-[length:var(--text-form)] text-[var(--ink-900)]">{o.label}</span>
                    {o.description && (
                      <span className="text-[length:var(--text-table-sm)] text-[var(--ink-700)]">{o.description}</span>
                    )}
                  </span>
                  {isSelected && (
                    <span className="inline-flex shrink-0 items-center gap-[2px] text-[length:var(--text-table-sm)] font-[var(--weight-medium)] text-[var(--blue-700)]">
                      {/* ⚠️ ต้องเป็นไอคอนที่อยู่ใน scripts/icons.txt — "check" ไม่มี เคยขึ้นเป็นคำว่า CHECK */}
                      <Icon name="check_circle" size={16} filled />
                      เลือกอยู่
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

export default SearchSelect;
